import { type FormEvent, useState } from 'react';
import {
  useAddCoHead,
  useCoHeads,
  useGrantRole,
  useRemoveCoHead,
  useRevokeRole,
  useRoles,
} from '../../api/admin';
import { type Person, useOrgStructure } from '../../api/org';
import { ROLE_LABEL, type RoleGrant, useCurrentUser } from '../../api/session';
import { EmployeePicker } from '../../components/EmployeePicker';
import { Icon } from '../../components/Icon';
import { EmptyState, ErrorState, LoadingState } from '../../components/states';
import { usePageTitle, usePersonDrawer } from '../../layout/shellContext';

const ROLE_HINT: Record<RoleGrant['role'], string> = {
  hr: 'Кабинеты сотрудников в своей области, изменение регламента',
  admin: 'Роли, соруководители, журнал безопасности; без доступа к кабинетам',
  auditor: 'Только чтение журналов',
};

function DepartmentSelect({
  value,
  onChange,
  label,
  allowAll,
}: {
  value: string;
  onChange: (id: string) => void;
  label: string;
  allowAll?: boolean;
}) {
  const structure = useOrgStructure();
  const options: { id: string; name: string; level: number }[] = [];
  if (structure.data) {
    const byId = new Map(structure.data.departments.map((d) => [d.id, d]));
    const walk = (ids: string[], level: number) => {
      for (const id of ids) {
        const department = byId.get(id);
        if (!department) continue;
        options.push({ id, name: department.name, level });
        walk(department.childIds, level + 1);
      }
    };
    walk(structure.data.rootIds, 0);
  }
  return (
    <label className="field">
      <span>{label}</span>
      <select className="select" value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="">{allowAll ? 'Вся компания' : 'Выберите подразделение'}</option>
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {`${'  '.repeat(option.level)}${option.name}`}
          </option>
        ))}
      </select>
    </label>
  );
}

function RolesPanel({ canManage }: { canManage: boolean }) {
  const roles = useRoles();
  const grant = useGrantRole();
  const revoke = useRevokeRole();
  const { open } = usePersonDrawer();
  const [employee, setEmployee] = useState<Person | null>(null);
  const [role, setRole] = useState<RoleGrant['role']>('hr');
  const [departmentId, setDepartmentId] = useState('');
  const [reason, setReason] = useState('');

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!employee || reason.trim().length < 3) return;
    grant.mutate(
      {
        employeeId: employee.id,
        role,
        reason: reason.trim(),
        ...(role === 'hr' && departmentId ? { departmentId } : {}),
      },
      {
        onSuccess: () => {
          setEmployee(null);
          setReason('');
          setDepartmentId('');
        },
      },
    );
  };

  return (
    <section className="panel page">
      <div className="panel-header" style={{ marginBottom: 0 }}>
        <h2>Роли</h2>
      </div>
      {canManage ? (
        <form className="page" onSubmit={submit}>
          <div className="form-row">
            <EmployeePicker label="Сотрудник" value={employee} onChange={setEmployee} />
            <label className="field">
              <span>Роль</span>
              <select
                className="select"
                value={role}
                onChange={(event) => setRole(event.target.value as RoleGrant['role'])}
              >
                <option value="hr">HR</option>
                <option value="admin">Администратор</option>
                <option value="auditor">Аудитор</option>
              </select>
            </label>
            {role === 'hr' ? (
              <DepartmentSelect label="Область HR" value={departmentId} onChange={setDepartmentId} allowAll />
            ) : null}
            <label className="field">
              <span>Основание</span>
              <input
                className="input"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="Например: приказ № 12"
              />
            </label>
          </div>
          <div className="panel-actions">
            <button
              type="submit"
              className="btn-primary"
              disabled={!employee || reason.trim().length < 3 || grant.isPending}
            >
              <Icon name="plus" size={14} />
              Выдать роль
            </button>
            <span className="muted">{ROLE_HINT[role]}</span>
          </div>
          {grant.isError ? <ErrorState title="Роль не выдана" error={grant.error} /> : null}
        </form>
      ) : null}

      {roles.isPending ? <LoadingState /> : null}
      {roles.isError ? <ErrorState error={roles.error} /> : null}
      {roles.data && !roles.data.items.length ? <EmptyState title="Ролей пока нет" /> : null}
      {roles.data?.items.length ? (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Сотрудник</th>
                <th>Роль</th>
                <th>Основание</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {roles.data.items.map((row) => (
                <tr key={row.id}>
                  <td>
                    {row.employeeId ? (
                      <button type="button" className="link" onClick={() => open(row.employeeId as string)}>
                        {row.displayName}
                      </button>
                    ) : (
                      <strong>{row.displayName}</strong>
                    )}
                    <small>{row.position ?? ''}</small>
                  </td>
                  <td>
                    <strong>{ROLE_LABEL[row.role]}</strong>
                    <small>{row.role === 'hr' ? (row.departmentName ?? 'вся компания') : ''}</small>
                  </td>
                  <td>{row.reason}</td>
                  <td className="number">
                    {canManage ? (
                      <button
                        type="button"
                        className="btn-icon ghost"
                        aria-label={`Отозвать роль у ${row.displayName}`}
                        disabled={revoke.isPending}
                        onClick={() => {
                          if (window.confirm(`Отозвать роль «${ROLE_LABEL[row.role]}» у ${row.displayName}?`))
                            revoke.mutate({ id: row.id });
                        }}
                      >
                        <Icon name="trash" />
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}

function CoHeadsPanel({ canManage }: { canManage: boolean }) {
  const coHeads = useCoHeads();
  const add = useAddCoHead();
  const remove = useRemoveCoHead();
  const [employee, setEmployee] = useState<Person | null>(null);
  const [departmentId, setDepartmentId] = useState('');
  const [reason, setReason] = useState('');

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!employee || !departmentId || reason.trim().length < 3) return;
    add.mutate(
      { employeeId: employee.id, departmentId, reason: reason.trim() },
      {
        onSuccess: () => {
          setEmployee(null);
          setReason('');
          setDepartmentId('');
        },
      },
    );
  };

  return (
    <section className="panel page">
      <div className="panel-header" style={{ marginBottom: 0 }}>
        <h2>Соруководители</h2>
      </div>
      {canManage ? (
        <form className="page" onSubmit={submit}>
          <div className="form-row">
            <DepartmentSelect label="Подразделение" value={departmentId} onChange={setDepartmentId} />
            <EmployeePicker label="Сотрудник" value={employee} onChange={setEmployee} />
            <label className="field">
              <span>Основание</span>
              <input className="input" value={reason} onChange={(event) => setReason(event.target.value)} />
            </label>
          </div>
          <div className="panel-actions">
            <button
              type="submit"
              className="btn-primary"
              disabled={!employee || !departmentId || reason.trim().length < 3 || add.isPending}
            >
              <Icon name="plus" size={14} />
              Назначить
            </button>
          </div>
          {add.isError ? <ErrorState title="Соруководитель не назначен" error={add.error} /> : null}
        </form>
      ) : null}
      {coHeads.isPending ? <LoadingState /> : null}
      {coHeads.isError ? <ErrorState error={coHeads.error} /> : null}
      {coHeads.data && !coHeads.data.items.length ? <EmptyState title="Соруководителей нет" /> : null}
      {coHeads.data?.items.length ? (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Подразделение</th>
                <th>Соруководитель</th>
                <th>Основание</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {coHeads.data.items.map((row) => (
                <tr key={row.id}>
                  <td>
                    <strong>{row.departmentName}</strong>
                  </td>
                  <td>
                    <strong>{row.fullName}</strong>
                    <small>{row.position ?? ''}</small>
                  </td>
                  <td>{row.reason}</td>
                  <td className="number">
                    {canManage ? (
                      <button
                        type="button"
                        className="btn-icon ghost"
                        aria-label={`Снять ${row.fullName}`}
                        disabled={remove.isPending}
                        onClick={() => {
                          if (
                            window.confirm(`Снять ${row.fullName} с соруководства «${row.departmentName}»?`)
                          )
                            remove.mutate({ id: row.id });
                        }}
                      >
                        <Icon name="trash" />
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}

export function AccessPage() {
  usePageTitle('Роли и соруководители');
  const me = useCurrentUser();
  const canManage = me.data?.roles.some((grant) => grant.role === 'admin') ?? false;
  return (
    <div className="page">
      <RolesPanel canManage={canManage} />
      <CoHeadsPanel canManage={canManage} />
    </div>
  );
}
